<template>
  <div class="flex-grow-1 d-flex flex-column overflow-hidden">

    <!-- <v-btn @click="processStamps">Process stamps…</v-btn> -->

    <input v-show="false" ref="upload" type="file" accept="image/*,image/heic" @click.stop />

    <error-dialog :status="loadMediaStatus" />
    <error-dialog :status="addMediaFromDataURLStatus" />
    <error-dialog :status="deleteMediaStatus" />

    <color-prompt
      v-if="promptColor"
      :value="promptColor.value"
      @input="v => promptColor.eager && promptColor.callback(v)"
      @close="v => { promptColor.callback(v); promptColor = null; }"
    />

    <custom-stamp-prompt
      v-if="promptStamp"
      ref="stamp"
      :opts="promptStamp"
      @save="(v, r) => addMediaFromDataURL(promptStamp.file.name, 'stamp', v).then(r)"
      @input="v => set({ env_stamp: v })"
      @close="promptStamp = null"
    />

    <sign-in-dialog ref="sign-in" v-model="showSignIn">
      <template v-slot:subtitle>Please sign in to upload custom media</template>
    </sign-in-dialog>

    <dialog-scroll-lock
      v-if="selected == 'LOGO' && !global.profile?.org_id"
      width="500"
      :value="true"
      @input="selected = null"
    >
      <v-card class="d-flex flex-column">
        <v-card-title class="d-block">
          Logo
          <v-btn class="float-right mr-n3 mt-n1" icon @click="selected = null">
            <v-icon v-html="mdiClose" />
          </v-btn>
        </v-card-title>
        <v-card-subtitle>
          Customizing / removing the logo is only available for <router-link target="_blank" :to="{ name: 'business' }">business customers</router-link>. Create an Ellacard organization to continue (no payment required).
        </v-card-subtitle>
        <v-divider />
        <v-card-text class="pt-5">
          <org-sign-up-form @cancel="selected = null" />
        </v-card-text>
      </v-card>
    </dialog-scroll-lock>

    <dialog-scroll-lock-loader
      v-else-if="selected == 'LOGO'"
      width="400"
      :loading="loadCustomMediaStatus.pending"
      :value="true"
      @input="selected = null"
    >
      <v-card class="d-flex flex-column" max-height="90vh">
        <div class="d-flex align-center pa-3">
          <div class="title pl-1">Logo</div>
          <v-spacer />
          <!-- Positive switch action:
                if (!orgHas('header_logo'))
                  promptImage(({ url }) => useSameLogo && !orgHas('email_footer_logo') ? set({ header_logo: url, email_footer_logo: url }) : set({ header_logo: url }));
          -->
          <v-switch
            class="mt-0 mr-2"
            hide-details
            :input-value="isActive('LOGO')"
            @click.native.capture.stop.prevent="
              () => isActive('LOGO') && set({
                  header_logo: orgHas('header_logo') ? orgSkin.header_logo : DEFAULT.header_logo,
                  email_footer_logo: orgHas('email_footer_logo') ? orgSkin.email_footer_logo : DEFAULT.email_footer_logo,
                  email_footer_url: orgHas('email_footer_url') ? orgSkin.email_footer_url : DEFAULT.email_footer_url
                })
            "
          />
          <v-btn small color="primary" @click="selected = null">Done</v-btn>
        </div>
        <v-tabs
          grow
          :value="[ 'header_logo', 'email_footer_logo' ].indexOf(logoKey)"
          @change="v => logoKey = [ 'header_logo', 'email_footer_logo' ][v]"
        >
          <v-tab class="text--label-active" style="text-transform: unset;">
            <v-icon class="mr-2" v-html="mdiCellphoneLink" />Web
          </v-tab>
          <v-tab class="text--label-active" style="text-transform: unset;">
            <v-icon class="mr-2" v-html="mdiTextBox" />Email
          </v-tab>
        </v-tabs>
        <v-divider />
        <div class="pt-4 px-4 pb-2">
          <v-img class="mx-auto" contain max-width="240" :aspect-ratio="16 / 9" :src="order.skin[logoKey]">
            <div
              v-if="!order.skin[logoKey]"
              class="fill-height d-flex align-center justify-center text-center font-italic grey--text"
            >
              No image
            </div>
          </v-img>
        </div>
        <v-card-text class="flex-shrink-1 py-6 overflow-auto">
          <v-row>
            <v-col cols="6">
              <v-card
                v-bind:class="{ active: !order.skin[logoKey] }"
                v-bind:style="{ outline: order.skin[logoKey] ? '2px dashed #E0E0E0 !important' : null }"
                hover
                :disabled="orgHas(logoKey)"
                @click="set({ [logoKey]: null })"
              >
                <v-responsive class="white pa-1" :aspect-ratio="16 / 9" />
              </v-card>
            </v-col>
            <v-col cols="6">
              <v-card
                style="outline: 2px dashed #E0E0E0 !important;"
                hover color="grey lighten-5"
                :disabled="orgHas(logoKey)"
                @click="promptImage(onUploadLogo)"
              >
                <v-responsive class="overflow-visible pa-1" :aspect-ratio="16 / 9">
                  <div class="fill-height d-flex align-center justify-center text-center text--label-active pa-3">
                    Add custom image…
                  </div>
                  <v-btn
                    style="right: -12px; bottom: -12px;"
                    absolute fab small
                    :loading="addMediaFromDataURLStatus.pending"
                  >
                    <v-icon color="primary" v-html="mdiUpload" />
                  </v-btn>
                </v-responsive>
              </v-card>
            </v-col>
            <v-col cols="6">
              <v-card
                class="v-card--link"
                v-bind:class="{ active: order.skin[logoKey] == DEFAULT[logoKey] }"
                flat
                :disabled="orgHas(logoKey)"
                @click="setLogo(DEFAULT[logoKey])"
              >
                <v-responsive class="overflow-visible pa-1" :aspect-ratio="16 / 9">
                  <v-img class="stamp-preview" contain :aspect-ratio="16 / 9" :src="DEFAULT[logoKey]" />
                </v-responsive>
              </v-card>
            </v-col>
            <v-col v-for="l in customMedia.logo" :key="l.url" class="relative" cols="6">
              <v-card
                class="v-card--link"
                v-bind:class="{ active: order.skin[logoKey] == l.url }"
                flat
                :disabled="orgHas(logoKey)"
                @click="setLogo(l.url)"
              >
                <v-responsive class="overflow-visible pa-1" :aspect-ratio="16 / 9">
                  <v-img class="stamp-preview" contain :aspect-ratio="16 / 9" :src="l.url" />
                </v-responsive>
              </v-card>
              <v-btn
                style="right: 0; bottom: 0;"
                absolute fab small
                :loading="deletingMedia == l.name"
                @click="deleteMedia('logo', l.name).then(x => {
                  if (x) {
                    if (!orgHas('header_logo') && order.skin.header_logo == l.url)
                      order.skin.header_logo = null;
                    if (!orgHas('email_footer_logo') && order.skin.email_footer_logo == l.url)
                      order.skin.email_footer_logo = null;
                  }
                })"
              >
                <v-icon color="warning" v-html="mdiDelete" />
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
        <div class="px-5">
          <v-checkbox
            v-model="useSameLogo"
            :disabled="orgHas('header_logo') || orgHas('email_footer_logo')"
            @change="setLogo(order.skin[logoKey])"
          >
            <template v-slot:label>
              <span class="text-body-2">Use same for web and email</span>
            </template>
          </v-checkbox>
        </div>
        <v-card-text class="overflow-hidden" style="height: 80px;">
          <v-text-field
            v-if="logoKey == 'email_footer_logo'"
            hide-details label="Logo hyperlink" placeholder="http…"
            :disabled="!order.skin.email_footer_logo || orgHas('email_footer_logo')"
            :rules="[ v => v && v.startsWith('http') ]"
            :value="order.skin.email_footer_logo ? order.skin.email_footer_url : '-'"
            @input="v => v && v.startsWith('http') && set({ email_footer_url: v })"
          />
        </v-card-text>
      </v-card>
    </dialog-scroll-lock-loader>

    <dialog-scroll-lock
      v-else-if="selected == 'BG'"
      content-class="fill-height" width="600"
      :value="true"
      @input="selected = null"
    >
      <v-card class="fill-height d-flex flex-column">
        <div class="d-flex align-center pa-3">
          <div class="title pl-1">Background</div>
          <v-spacer />
          <!-- Positive action: promptImage(onUploadBackground); -->
          <v-switch
            v-if="!orgHas('page_background_color')"
            class="mt-0 mr-2"
            hide-details
            :input-value="isActive('BG')"
            @click.native.capture.stop.prevent="
              () => isActive('BG') && set({
                  page_background_color: DEFAULT.page_background_color,
                  page_background_img: DEFAULT.page_background_img
                })
            "
          />
          <v-btn small color="primary" @click="selected = null">Done</v-btn>
        </div>
        <v-divider />
        <div class="px-6 py-5">
          <div class="mx-auto relative" style="max-width: 200px;">
            <v-menu absolute offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-card v-bind="attrs" v-on="on" hover :color="order.skin.page_background_color || '#FFF'">
                  <v-responsive :aspect-ratio="16 / 9">
                    <component
                      v-if="order.skin.page_background_img"
                      :is="order.skin.page_background_img.endsWith('.mp4') ? 'video' : 'img'"
                      :key="order.skin.page_background_img"
                      class="absolute loading-icon"
                      style="object-fit: cover;"
                      width="100%" height="100%" playsinline autoplay muted loop
                      :src="order.skin.page_background_img"
                      @load="e => e.target.classList.add('loaded')"
                      @loadeddata="e => e.target.classList.add('loaded')"
                    />
                  </v-responsive>
                </v-card>
              </template>
              <v-list dense>
                <v-list-item v-if="order.skin.page_background_img" @click="set({ page_background_img: null })">
                  <v-icon class="mr-3" v-html="mdiClose" />
                  <v-list-item-title>Remove image</v-list-item-title>
                </v-list-item>
                <v-list-item v-else-if="!noCustomMedia" @click="promptImage(onUploadBackground)">
                  <v-icon class="mr-3" v-html="mdiUpload" />
                  <v-list-item-title>Upload image…</v-list-item-title>
                </v-list-item>
                <v-list-item
                  v-if="!orgHas('page_background_color')"
                  @click="promptColor = {
                    value: order.skin.page_background_color,
                    eager: true,
                    callback: v => set({ page_background_color: v })
                  }"
                >
                  <v-icon class="mr-3" v-html="mdiPalette" />
                  <v-list-item-title>Set color…</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-btn
              v-if="!orgHas('page_background_color')"
              style="right: -8px; bottom: -8px;"
              absolute
              :color="order.skin.page_background_color || '#FFF'"
              @click="promptColor = {
                value: order.skin.page_background_color,
                eager: true,
                callback: v => set({ page_background_color: v })
              }"
            >
              <v-icon
                :color="getBrightness(order.skin.page_background_color || '#FFF') < 160 ? '#F5F5F5' : '#344558'"
                v-html="mdiPalette"
              />
            </v-btn>
          </div>
        </div>

        <v-divider />

        <!-- TODO: conditionally show / hide this based on seasonality -->
        <!--
        <theme-filter-chips v-model="filter" />
        <v-divider />
        -->

        <template v-if="loadMediaStatus.pending || loadCustomMediaStatus.pending">
          <v-progress-linear indeterminate />
          <v-spacer />
        </template>

        <template v-else>
          <div class="mt-2 mx-2 text--label-active">Filters</div>
          <side-scroller>
            <v-chip
              v-for="[ tag, icon ], i in BG_FILTERS" :key="'bg-' + tag"
              class="ma-1"
              v-bind:class="{ 'primary--text': bgFilter == i }"
              filter
              :input-value="bgFilter == i"
              @click="bgFilter = bgFilter == i ? 0 : i"
            >
              {{tag}}
              <v-icon v-if="icon" class="ml-1" v-bind:class="{ 'icon-animated': tag == 'Animated' }" v-html="icon" />
            </v-chip>
          </side-scroller>
          <v-virtual-scroll-grid
            class="flex-grow-1"
            bench="1"
            :items="
              bgFilter ?
              media.background.filter(x => x.tags.includes(BG_FILTERS[bgFilter][0])) :
              [
                { default: true, url: null, color: DEFAULT.page_background_color },
                ...(noCustomMedia ? [] : [ null, ...customMedia.background ]),
                ...media.background
              ]
            "
            :n="$vuetify.breakpoint.xsOnly ? 2 : 3"
            :aspect-ratio="16 / 9"
            :padding="12"
          >
            <template v-slot:default="{ item, index }">
              <template v-if="item">
                <v-card
                  v-bind:class="{ active: order.skin.page_background_img == item.url }"
                  hover
                  @click="set({
                    page_background_color: orgHas('page_background_color') ? orgSkin.page_background_color : item.color || DEFAULT.page_background_color,
                    page_background_img: orgHas('page_background_img') ? orgSkin.page_background_img : item.url
                  })"
                >
                  <v-responsive :aspect-ratio="16 / 9">
                    <component
                      v-if="item.url"
                      :is="item.name.endsWith('.mp4') ? 'video' : 'img'"
                      class="absolute"
                      style="object-fit: cover;"
                      width="100%" height="100%" playsinline autoplay muted loop
                      :src="getBackgroundPreview(item)"
                    />
                  </v-responsive>
                </v-card>
                <v-btn
                  v-if="!item.default"
                  style="right: 0; bottom: 0;"
                  absolute fab small
                  :loading="deletingMedia == item.name"
                  @click="deleteMedia('background', item.name).then(x => {
                    if (x && (order.skin.page_background_img == item.url))
                      order.skin.page_background_img = null;
                  })"
                >
                  <v-icon color="warning" v-html="mdiDelete" />
                </v-btn>
              </template>
              <v-card
                v-else
                v-blur
                class="relative"
                style="outline: 2px dashed #E0E0E0 !important;"
                hover color="grey lighten-5"
                @click="promptImage(onUploadBackground)"
              >
                <v-responsive :aspect-ratio="16 / 9">
                  <div class="fill-height d-flex align-center justify-center text-center text--label-active pa-3">
                    Add custom image…
                  </div>
                </v-responsive>
                <v-btn
                  style="right: -12px; bottom: -12px;"
                  absolute fab small
                  :loading="addMediaFromDataURLStatus.pending"
                >
                  <v-icon color="primary" v-html="mdiUpload" />
                </v-btn>
              </v-card>
            </template>
          </v-virtual-scroll-grid>
        </template>
      </v-card>
    </dialog-scroll-lock>

    <dialog-scroll-lock v-else-if="selected == 'CF'" width="300" :value="true" @input="selected = null">
      <v-card class="overflow-hidden">
        <div class="d-flex align-center pa-3">
          <div class="title pl-1">Confetti</div>
          <v-spacer />
          <v-switch
            class="mt-0 mr-2"
            hide-details
            :input-value="isActive('CF')"
            @change="() => { confetti.show = !confetti.show; setConfetti(); }"
          />
          <v-btn small color="primary" @click="selected = null">Done</v-btn>
        </div>
        <v-divider />
        <div class="px-4 pt-3">
          <div class="d-flex align-center mb-6">
            <div class="v-label theme--light" v-bind:class="{ 'v-label--is-disabled': !confetti.show }">Preset</div>
            <v-select
              class="ml-4 mt-0 overline"
              hide-details dense solo
              :items="[ 'None', ...EFFECT_NAMES ]"
              :value="selectedEffectPreset"
              @input="selectEffectPreset"
            >
              <template v-slot:item="{ item }">
                <div class="overline short">{{item}}</div>
              </template>
            </v-select>
          </div>
          <v-slider
            v-for="label in [ 'Density', 'Size', 'Wind', 'Gravity' ]" :key="label"
            min="0" max="1" step=".01"
            :label="label"
            :disabled="!confetti.show"
            v-model="confetti.opts[ label.toLowerCase() ]"
            @end="setConfetti"
          />
          <div class="v-label theme--light mt-1" v-bind:class="{ 'v-label--is-disabled': !confetti.show }">Colors</div>
        </div>
        <div class="overflow-auto" style="height: 152px; padding: 6px;">
          <v-btn
            v-for="c, i in confetti.opts.colors" :key="i + '-' + c"
            v-blur
            class="ma-2 relative"
            v-bind:style="{ background: c }"
            fab x-small
            :elevation="confetti.show ? null : 1"
            @click="
              () => {
                if (confetti.show)
                  promptColor = {
                    value: c,
                    callback: v => { $set(confetti.opts.colors, i, v); setConfetti(); }
                  }
              }
            "
          >
            <v-btn
              v-if="confetti.show && (confetti.opts.colors.length > 1)"
              style="top: -28px; right: -16px; transform: scale(.5);"
              absolute dark x-small fab
              @click.stop="
                () => {
                  if (!confetti.show || confetti.opts.colors.length == 1)
                    return;
                  confetti.opts.colors.splice(i, 1);
                  setConfetti();
                }
              "
            >
              <v-icon v-html="mdiClose" />
            </v-btn>
          </v-btn>
          <v-btn
            class="ma-1"
            icon color="primary"
            :disabled="!confetti.show"
            @click="
              promptColor = {
                value: '#7fa8d7',
                callback: v => { confetti.opts.colors.push(v); setConfetti(); }
              }
            "
          >
            <v-icon v-html="mdiPlus" />
          </v-btn>
        </div>
      </v-card>
    </dialog-scroll-lock>

    <dialog-scroll-lock
      v-else-if="selected == 'STMP'"
      content-class="fill-height" width="600"
      :value="true"
      @input="selected = null"
    >
      <v-card class="fill-height d-flex flex-column">
        <div class="d-flex align-center pa-3">
          <div class="title pl-1">Stamp</div>
          <v-spacer />
          <!-- You had previously made the on switch prompt for an image: promptImage(img => promptStamp = { ...img, scale: true, border: true }); -->
          <v-switch
            class="mt-0 mr-2"
            hide-details
            :input-value="isActive('STMP')"
            @click.native.capture.stop.prevent="() => isActive('STMP') && set({ env_stamp: DEFAULT.env_stamp })"
          />
          <v-btn small color="primary" @click="selected = null">Done</v-btn>
        </div>
        <v-divider />
        <v-card-text class="pt-5">
          <v-row>
            <v-col class="mx-auto" cols="4" sm="3">
              <v-menu absolute offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    <v-card
                      v-if="!order.skin.env_stamp"
                      style="outline: 2px dashed #E0E0E0 !important;"
                      hover
                    >
                      <v-responsive class="white" :aspect-ratio="1" />
                    </v-card>
                    <v-responsive v-else v-bind="attrs" v-on="on" class="overflow-visible" :aspect-ratio="1">
                      <img class="stamp-preview" width="100%" height="100%" :src="order.skin.env_stamp" />
                    </v-responsive>
                  </div>
                </template>
                <v-list v-if="order.skin.env_stamp || !noCustomMedia" dense>
                  <v-list-item v-if="order.skin.env_stamp" @click="set({ env_stamp: null })">
                    <v-icon class="mr-3" v-html="mdiClose" />
                    <v-list-item-title>Remove image</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-else
                    @click="promptImage(img => promptStamp = { ...img, scale: true, border: true })"
                  >
                    <v-icon class="mr-3" v-html="mdiUpload" />
                    <v-list-item-title>Upload image…</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider />

        <template v-if="loadMediaStatus.pending || loadCustomMediaStatus.pending">
          <v-progress-linear indeterminate />
          <v-spacer />
        </template>

        <template v-else>
          <div class="mt-2 mx-2 text--label-active">Filters</div>
          <side-scroller>
            <v-chip
              v-for="[ tag, icon ], i in STAMP_FILTERS" :key="'stmp-' + tag"
              class="ma-1"
              v-bind:class="{ 'primary--text': stampFilter == i }"
              filter
              :input-value="stampFilter == i"
              @click="stampFilter = stampFilter == i ? 0 : i"
            >
              {{tag}}
              <v-icon v-if="icon" class="ml-1" v-html="icon" />
            </v-chip>
          </side-scroller>
          <v-virtual-scroll-grid
            :key="stampFilter"
            class="flex-grow-1"
            bench="1"
            :items="
              stampFilter ?
              media.stamp.filter(x => x.tags.includes(STAMP_FILTERS[stampFilter][0])) :
              [ null, ...(noCustomMedia ? [] : [ null, ...customMedia.stamp ]), ...media.stamp ]
            "
            :n="$vuetify.breakpoint.xsOnly ? 3 : 4"
            :aspect-ratio="1"
            :padding="12"
          >
            <template v-slot:default="{ item, index }">
              <v-card
                v-if="(index == 0) && !stampFilter"
                v-bind:class="{ active: !order.skin.env_stamp }"
                v-bind:style="{ outline: order.skin.env_stamp ? '2px dashed #E0E0E0 !important' : null }"
                hover
                @click="set({ env_stamp: null })"
              >
                <v-responsive class="white" :aspect-ratio="1" />
              </v-card>
              <v-card
                v-else-if="!noCustomMedia && (index == 1) && !stampFilter"
                style="outline: 2px dashed #E0E0E0 !important;"
                hover color="grey lighten-5"
                @click="promptImage(img => promptStamp = { ...img, scale: true, border: true })"
              >
                <v-responsive class="overflow-visible" :aspect-ratio="1">
                  <div class="fill-height d-flex align-center justify-center text-center text--label-active pa-3">
                    Add custom image…
                  </div>
                  <v-btn style="right: -12px; bottom: -12px;" absolute fab small>
                    <v-icon color="primary" v-html="mdiUpload" />
                  </v-btn>
                </v-responsive>
              </v-card>
              <template v-else>
                <v-card class="v-card--link" v-bind:class="{ active: order.skin.env_stamp == item.url }" flat>
                  <v-responsive class="overflow-visible" :aspect-ratio="1">
                    <img
                      class="stamp-preview"
                      width="100%" height="100%" loading="lazy"
                      :src="item.url"
                      @click="set({ env_stamp: item.url })"
                    />
                  </v-responsive>
                </v-card>
                <v-btn
                  v-if="!item.default"
                  style="right: 0; bottom: 0;"
                  absolute fab small
                  :loading="deletingMedia == item.name"
                  @click="deleteMedia('stamp', item.name).then(x => {
                    if (x) {
                      if (order.skin.env_stamp == item.url)
                        order.skin.env_stamp = null;
                    }
                  })"
                >
                  <v-icon color="warning" v-html="mdiDelete" />
                </v-btn>
              </template>
            </template>
          </v-virtual-scroll-grid>
        </template>
      </v-card>
    </dialog-scroll-lock>

    <music-library-dialog
      v-else-if="selected == 'MUS'"
      :is-active="isActive('MUS')"
      :no-custom-media="noCustomMedia"
      :value="order.skin.music"
      @input="v => set({ music: v })"
      @close="selected = null"
    />

    <dialog-scroll-lock v-else-if="selected == 'ENV'" width="600" :value="true" @input="selected = null">
      <v-card>
        <div class="d-flex align-center pa-3 relative" style="z-index: 2;">
          <div class="title pl-1">Envelope colors</div>
          <v-spacer />
          <v-btn
            class="mr-3"
            small
            @click="() => {
              const bg = COLORS[ Math.floor(Math.random() * COLORS.length) ];
              set({
                env_background_color: bg,
                env_font_color: getBrightness(bg) < 160 ? '#FEFEFE' : '#1A237E'
              });
            }"
          >
            <v-icon v-html="mdiShuffleVariant" />
            <span class="btn-tooltip bottom">Randomize</span>
          </v-btn>
          <!--
          <v-switch
            v-if="!orgHas('env_font_color') && !orgHas('env_background_color')"
            class="mt-0 mr-2"
            hide-details
            :input-value="isActive('ENV')"
            @change="
              () => {
                if (isActive('ENV')) {
                  set({
                    env_background_color: DEFAULT.env_background_color,
                    env_font_color: DEFAULT.env_font_color
                  });
                } else {
                  const bg = COLORS[ Math.floor(Math.random() * COLORS.length) ];
                  set({
                    env_background_color: bg,
                    env_font_color: getBrightness(bg) < 160 ? '#FEFEFE' : '#1A237E'
                  });
                }
              }
            "
          />
          -->
          <v-btn small color="primary" @click="selected = null">Done</v-btn>
        </div>

        <div class="hidden-sm-and-up mb-4 px-4">
          <div class="mx-auto d-flex justify-center" style="max-width: 300px; aspect-ratio: 7 / 5; position: relative;">
            <envelope
              :key="order.skin.env_background_color + order.skin.env_font_color"
              style="position: absolute; inset: -20% 0 -20% 0;"
              :from="envFrom || '[&nbsp;sender&nbsp;name&nbsp;]'"
              :to="envTo || '[&nbsp;recipient&nbsp;name&nbsp;]'"
              :skin="order.skin"
            />
            <div v-if="envLowContrast" class="btn-tooltip persistent prominent bottom warning--text" style="top: unset; bottom: 8px;">
              <v-icon class="mr-2" small color="warning" v-html="mdiAlert" /> Low contrast warning
            </div>
          </div>
        </div>

        <div class="d-flex justify-center align-center">
          <div class="mx-2 flex-grow-1 flex-sm-grow-0" style="width: 144px;">
            <div class="text-center text--label-active mb-2">
              <v-icon class="mr-1" v-html="iconPathEnvBackground" /> Background
            </div>
            <v-expand-transition>
              <div v-if="$vuetify.breakpoint.smAndUp || !showMoreColors">
                <v-btn
                  v-for="color in COLORS" :key="color"
                  v-blur fab x-small class="ma-2"
                  v-bind:style="{
                    'background': color,
                    'border': order.skin.env_background_color == color ? '1px solid rgba(0, 0, 0, 0.12)' : null,
                    'box-shadow': order.skin.env_background_color == color ? `0 0 8px 4px ${color} !important` : null
                  }"
                  :elevation="orgHas('env_background_color') ? 1 : null"
                  @click="orgHas('env_background_color') || set({ env_background_color: color })"
                />
              </div>
            </v-expand-transition>
          </div>
          <v-divider v-if="!showMoreColors" class="hidden-sm-and-up" vertical />
          <div class="hidden-xs-only flex-grow-1">
            <div class="mt-8 mx-3 d-flex justify-center" style="aspect-ratio: 7 / 5; position: relative;">
              <envelope
                :key="order.skin.env_background_color + order.skin.env_font_color"
                style="position: absolute; inset: -20% 0 -20% 0;"
                :from="envFrom || '[&nbsp;sender&nbsp;name&nbsp;]'"
                :to="envTo || '[&nbsp;recipient&nbsp;name&nbsp;]'"
                :skin="order.skin"
              />
              <div v-if="envLowContrast" class="btn-tooltip persistent prominent bottom warning--text" style="top: unset; bottom: 8px;">
                <v-icon class="mr-2" small color="warning" v-html="mdiAlert" /> Low contrast warning
              </div>
            </div>
          </div>
          <div class="mx-2 flex-grow-1 flex-sm-grow-0 text-right" style="width: 144px;">
            <div class="text-center text--label-active mb-2">
              <v-icon class="mr-1" v-html="iconPathEnvToFrom" /> Text
            </div>
            <v-expand-transition>
              <div v-if="$vuetify.breakpoint.smAndUp || !showMoreColors">
                <v-btn
                  v-for="color in COLORS" :key="color"
                  v-blur fab x-small class="ma-2"
                  v-bind:style="{
                    'background': color,
                    'border': order.skin.env_font_color == color ? '1px solid rgba(0, 0, 0, 0.12)' : null,
                    'box-shadow': order.skin.env_font_color == color ? `0 0 8px 4px ${color} !important` : null
                  }"
                  :elevation="orgHas('env_font_color') ? 1 : null"
                  @click="orgHas('env_font_color') || set({ env_font_color: color })"
                />
              </div>
            </v-expand-transition>
          </div>
        </div>

        <div class="pa-2 text-center">
          <v-btn
            class="pl-2"
            style="text-transform: unset; letter-spacing: unset;"
            text color="primary"
            @click="showMoreColors = !showMoreColors"
          >
            <v-icon class="mr-1" v-html="showMoreColors ? mdiMenuUp : mdiMenuDown" />
            <span class="text--label-active primary--text">{{showMoreColors ? 'Hide' : 'Show'}} color pickers</span>
          </v-btn>
        </div>

        <v-expand-transition>
          <div v-if="showMoreColors" class="d-flex">
            <v-color-picker
              style="max-width: 50%;"
              mode="rgba"
              :value="vColorPickerValue(order.skin.env_background_color)"
              :disabled="orgHas('env_background_color')"
              @input="v => set({ env_background_color: v })"
            />
            <v-divider style="z-index: 1;" vertical />
            <v-color-picker
              style="max-width: 50%;"
              mode="rgba"
              :value="vColorPickerValue(order.skin.env_font_color)"
              :disabled="orgHas('env_font_color')"
              @input="v => set({ env_font_color: v })"
            />
          </div>
        </v-expand-transition>
      </v-card>
    </dialog-scroll-lock>

    <div class="flex-grow-1 relative overflow-auto">

      <div
        v-if="!isCancelled"
        class="absolute overflow-visible d-flex flex-column pa-4 pl-8"
        style="grid-gap: 8px; z-index: 2; right: 0;"
      >

        <slot name="top-right"></slot>

        <div
          v-if="!(orgHas('page_background_color') && orgHas('page_background_img') && orgHas('open_effect'))"
          class="btn-group btn-group-vertical d-flex flex-column"
        >
          <v-btn
            v-if="!(orgHas('page_background_color') && orgHas('page_background_img'))"
            v-blur
            class="btn-top"
            v-bind:class="{ active: isActive('BG') }"
            fab dark color="red lighten-1"
            @click="
              () => {
                if (orgHas('page_background_img'))
                  promptColor = {
                    value: order.skin.page_background_color,
                    eager: true,
                    callback: v => set({ page_background_color: v })
                  }
                else
                  selected = 'BG';
              }
            "
          >
            <v-icon v-html="mdiImage" />
            <span class="btn-tooltip left" v-bind:class="{ persistent: global.showHelp }">
              Background{{orgHas('page_background_img') && !orgHas('page_background_color') ? ' color' : ''}}
            </span>
          </v-btn>

          <v-btn
            v-if="!orgHas('open_effect')"
            v-blur
            class="btn-top"
            v-bind:class="{ active: isActive('CF') }"
            fab dark color="red lighten-1"
            @click="selected = 'CF'"
          >
            <v-icon v-html="mdiGrain" />
            <span class="btn-tooltip left" v-bind:class="{ persistent: global.showHelp }">Confetti</span>
          </v-btn>
        </div>

        <div class="btn-group btn-group-vertical d-flex flex-column">
          <v-btn
            v-if="!orgHas('env_stamp')"
            v-blur
            class="btn-top" v-bind:class="{ active: isActive('STMP') }"
            fab dark color="amber darken-3"
            @click="selected = 'STMP'"
          >
            <v-icon v-html="iconPathEnvStamp" />
            <span class="btn-tooltip left" v-bind:class="{ persistent: global.showHelp }">Stamp</span>
          </v-btn>

          <v-btn
            v-if="!(orgHas('env_font_color') && orgHas('env_background_color'))"
            v-blur
            class="btn-top" v-bind:class="{ active: isActive('ENV') }"
            fab dark color="amber darken-3"
            @click="selected = 'ENV'"
          >
            <v-icon v-html="iconPathEnvColor" />
            <span class="btn-tooltip left" v-bind:class="{ persistent: global.showHelp }">Envelope colors</span>
          </v-btn>

          <font-picker-btn
            v-if="!orgHas('env_font')"
            ref="font-picker-btn"
            :fonts="FONTS"
            :left="true"
            :value="order.skin.env_font"
            @input="v => set({ env_font: v })"
          >
            <template v-slot="{ on }">
              <v-btn
                v-blur v-on="on"
                class="btn-top" v-bind:class="{ active: isActive('FONT') }"
                fab dark color="amber darken-3"
              >
                <v-icon v-html="iconPathEnvFont" />
                <span class="btn-tooltip left" v-bind:class="{ persistent: global.showHelp }">Envelope font</span>
              </v-btn>
            </template>
            <template v-slot:header>
              <div class="flex-grow-0 d-flex align-center pa-3">
                <div class="title pl-2" style="margin-top: 2px;">Font</div>
                <v-spacer />
                <v-switch
                  class="mt-0 mr-2"
                  hide-details
                  :input-value="isActive('FONT')"
                  @change="set({ env_font: isActive('FONT') ? DEFAULT.env_font : FONTS.filter(x => x != DEFAULT.env_font)[ Math.floor(Math.random() * (FONTS.length - 1)) ] })"
                />
                <v-btn small color="primary" @click="$refs['font-picker-btn'].open = false">Done</v-btn>
              </div>
              <v-divider />
            </template>
          </font-picker-btn>
        </div>

        <v-btn
          v-if="!orgHas('music')"
          class="btn-top"
          v-bind:class="{ active: isActive('MUS') }"
          fab dark color="lime darken-1"
          :input-value="selected == 'MUS'"
          @click="selected = 'MUS'"
        >
          <v-icon v-html="mdiMusic" />
          <span class="btn-tooltip left" v-bind:class="{ persistent: global.showHelp }">Music</span>
        </v-btn>

        <v-btn
          v-if="!orgHasLogo"
          v-blur
          class="btn-top"
          v-bind:class="{ active: isActive('LOGO') }"
          fab dark color="blue-grey lighten-1"
          @click="selected = 'LOGO'"
        >
          <v-icon v-html="mdiTag" />
          <span class="btn-tooltip left" v-bind:class="{ persistent: global.showHelp }">Logo</span>
        </v-btn>

      </div>

      <div
        class="fill-height relative"
        v-bind:class="{ 'theme-interactive': !isCancelled, 'hover-logo': !orgHasLogo }"
        style="padding-right: 72px; z-index: 0; min-height: 446px;"
        v-bind:style="{ 'background-color': hideBackgroundConfetti ? null : order.skin.page_background_color }"
      >
        <confetti
          v-if="order.skin.open_effect && !hideBackgroundConfetti"
          :key="order.skin.open_effect"
          class="absolute-fill overflow-hidden"
          style="z-index: 2;"
          :effect="order.skin.open_effect"
          :active="true"
        />
        <card-background
          v-if="order.skin.page_background_img && !hideBackgroundConfetti"
          :key="order.skin.page_background_img"
          :src="order.skin.page_background_img"
        />
        <div
          class="absolute ma-4 header-logo"
          v-bind:class="{ disabled: disableLogo }"
          style="min-height: 64px; min-width: 64px;"
          @click="!disableLogo && click('logo')"
        >
          <img v-if="order.skin.header_logo" class="d-block" height="64" :src="order.skin.header_logo" />
        </div>
        <div class="pa-4 fill-height mx-auto relative">
          <envelope
            ref="env"
            class="fill-height"
            :from="envFrom"
            :to="envTo"
            :to-multiple-recipients="order.multiple_recipients"
            :skin="order.skin"
            :edit-stamp="!isCancelled && !orgHas('env_stamp')"
            :edit-from="!isCancelled && !disableFrom"
            :edit-to="!isCancelled && !order.multiple_recipients && !disableTo"
            :edit-to-multiple-recipients="!isCancelled && (order.type != 'INVITATION') && !disableTo && !disableMultipleRecipients"
            :edit-to-multiple-recipients-label="`Send ${(order.type == 'GROUP') ? 'the signed / completed' : 'this'} card to multiple people`"
            :edit-to-multiple-recipients-disabled="(order.state == 'COMPLETED') && order.send_started_at && order.multiple_recipients && (order.info.recipient_groups.filter(x => x.targets.some(y => (deliveries?.byTarget[ y.toLowerCase() ] || []).length)).length >= 2)"
            :dirty="(order.state != 'PENDING') || order.dirty.includes('envelope')"
            @update:from="v => order.info.from = v || ''"
            @update:to="v => !order.multiple_recipients && (order.info.to = v || '')"
            @update:multiple-recipients="setMultipleRecipients"
            @click:stamp="click('stamp')"
          >
            <template v-if="order.skin.music || !isCancelled" v-slot:below>
              <div class="absolute-fill py-3 d-flex justify-center">
                <div v-if="order.skin.music" class="btn-group d-flex" style="max-width: 100%;">
                  <tooltip-btn msg="Music preview" top v-slot="{ on }">
                    <audio-preview-btn
                      class="flex-shrink-1 overflow-hidden"
                      v-bind:class="{ 'btn-group-right': !orgHas('music') && !isCancelled }"
                      color="primary" dark
                      :height="$vuetify.breakpoint.smAndUp ? 56 : null"
                      :name="musicName"
                      :src="order.skin.music"
                      v-on="on"
                    />
                  </tooltip-btn>
                  <tooltip-btn v-if="!orgHas('music') && !isCancelled" msg="Remove music" top v-slot="{ on }">
                    <v-btn
                      class="btn-group-left px-2 px-sm-4"
                      style="min-width: unset;"
                      rounded color="primary" dark
                      :height="$vuetify.breakpoint.smAndUp ? 56 : null"
                      @click="set({ music: null })"
                      v-on="on"
                    >
                      <v-icon v-html="mdiClose" />
                    </v-btn>
                  </tooltip-btn>
                </div>
                <v-btn v-else-if="!orgHas('music')" color="primary" rounded :height="$vuetify.breakpoint.smAndUp ? 56 : null" @click="selected = 'MUS'">
                  <v-icon class="mr-2" v-html="mdiMusic" /> Click to add music…
                </v-btn>
              </div>
            </template>
          </envelope>
        </div>
      </div>
    </div>

    <div v-if="!noFooter" class="flex-grow-0 relative btn-group" style="z-index: 1;">
      <tooltip-btn
        v-if="!isCancelled"
        :msg="`${global.showThemePresets ? 'Hide' : 'Show'} presets`"
        top
        v-slot="{ on }"
      >
        <v-btn
          v-blur v-on="on"
          class="btn-group-left btn-group-bottom px-4"
          style="bottom: 100%;"
          absolute color="grey lighten-5"
          @click="global.showThemePresets = !global.showThemePresets"
        >
          <v-icon small color="primary" v-html="global.showThemePresets ? mdiArrowCollapseDown : mdiArrowCollapseUp" />
        </v-btn>
      </tooltip-btn>

    </div>
    <v-expand-transition>
      <div
        v-if="!noFooter && global.showThemePresets && !isCancelled"
        class="grey lighten-5"
        style="
          z-index: 2;
          box-shadow: 0px 3px 1px -2px rgb(0 0 0 / 20%), 0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 1px 5px 0px rgb(0 0 0 / 12%);
        "
      >
        <select-theme-ribbon
          class="overflow-auto pa-3"
          style="max-width: 100%;"
          font-size=".7rem" width="70"
          :order="order" :org-skin="orgSkin"
          @input="v => set({ ...v, page_background_img: null })"
        />
      </div>
    </v-expand-transition>

  </div>
</template>


<style scoped>
.blob-container {
  padding: 12px;
  outline: 1px solid #1976d2;
  border-radius: 4px;
  cursor: pointer;
}

.blob-container.absolute {
  inset: -12px;
}

.blob-container.dark {
  outline-color: #FFF;
}

.blob-container.error {
  outline-color: #ff5252 !important;
  outline-width: 2px !important;
}

.blob-container:hover,
.blob-container:focus-within,
.blob-container.active {
  outline-width: 2px;
  cursor: text;
  box-shadow: 0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12);
}

.blob-container:hover:not(:focus-within) {
  background-color: rgb(0 0 0 / 2%);
}

.blob-container:hover:has(.blob-label-top),
.blob-container:focus-within:has(.blob-label-top),
.blob-container.active:has(.blob-label-top) {
  border-top-left-radius: 0;
}

.blob-container:hover:has(.blob-label-bottom),
.blob-container:focus-within:has(.blob-label-bottom),
.blob-container.active:has(.blob-label-bottom) {
  border-bottom-left-radius: 0;
}

/*.to-blob-container:hover:not(:focus-within) {
  background: #1976d211;
}*/

.blob-container .blob-label {
  display: none;
  position: absolute;
  left: -2px;
  background: #1976d2;
  color: #FFF;
  max-width: calc(100% - 12px);
  cursor: unset;
}

.blob-container.dark .blob-label {
  color: #344558;
  outline-color: #FFF;
  background: #FFF;
}

.blob-container .blob-label.blob-label-top {
  padding: 4px 8px;
  bottom: 100%;
  border-radius: 4px 4px 0 0;
  transform-origin: bottom left;
}

.blob-container .blob-label.blob-label-bottom {
  padding: 16px;
  top: 100%;
  border-radius: 0 0 4px 4px;
  transform-origin: top left;
}

.blob-container:hover .blob-label,
.blob-container:focus-within .blob-label,
.blob-container.active .blob-label {
  display: block;
}

.label {
  top: 12px;
  left: calc(100% + 12px);
  white-space: nowrap;
}
.pulse:before {
  background-color: #1976d2;
  animation: btn-pulse 1.6s ease-in-out infinite;
}
@keyframes btn-pulse {
  0% { opacity: 0; }
  50% { opacity: .12; }
  100% { opacity: 0; }
}

/*
.btn-top:after {
  transition: opacity .4s;
  opacity: 0;
  content: '';
  position: absolute;
  left: -13px;
  background: #4caf50;
  width: 10px;
  height: 10px;
  border-radius: 5px;
  box-shadow: 0px 3px 5px -1px rgb(0 0 0 / 20%), 0px 6px 10px 0px rgb(0 0 0 / 14%), 0px 1px 18px 0px rgb(0 0 0 / 12%);
}
.btn-top.active:after {
  opacity: 1;
}
*/

.stamp-preview {
  display: block;
  transition: filter .12s;
}
.stamp-preview:hover {
  filter:
    drop-shadow(0 5px 2px rgb(0 0 0 / 10%))
    drop-shadow(0 8px 9px rgb(0 0 0 / 10%))
    drop-shadow(0 3px 12px rgb(0 0 0 / 10%));
}

.btn-top.v-btn.v-btn--disabled {
  background-color: rgba(0, 0, 0, 0.12) !important;
}
</style>


<style>
.v-color-picker {
  border-radius: 0;
}
.v-card--link.active {
  outline: 2px solid #1976D2;
}
.v-card--link.active:before {
  opacity: 0.08;
}

.theme-interactive .env.closed > .outside-back,
.theme-interactive .header-logo {
  z-index: 7;
  pointer-events: auto;
}

.theme-interactive.hover-logo .header-logo:not(.disabled) {
  outline: 1px solid #1976d2;
  outline-offset: 4px;
  border-radius: 4px;
}
.theme-interactive.hover-logo .header-logo:not(.disabled):hover {
  outline-width: 2px;
  border-bottom-left-radius: 0;
}
.theme-interactive.hover-logo .header-logo:not(.disabled):hover:after {
  content: 'Logo';
  position: absolute;
  background-color: #1976d2;
  top: calc(100% + 6px);
  left: -4px;
  outline: inherit;
  color: #FFF;
  max-width: calc(100% - 12px);
  cursor: unset;
  font-size: 12px;
  line-height: 15px;
  border-radius: 0 0 4px 4px;
  padding: 4px 8px;
}
</style>


<script>
import AudioPreviewBtn from '@/components/AudioPreviewBtn.vue';
import CardBackground from '@/components/CardBackground.vue';
import ColorPrompt from '@/components/ColorPrompt.vue';
import Confetti from '@/components/Confetti.vue';
import CustomStampPrompt from '@/components/CustomStampPrompt.vue';
import Envelope from '@/components/Envelope.vue';
import FontPickerBtn from '@/components/FontPickerBtn.vue';
import MusicLibraryDialog from '@/components/MusicLibraryDialog.vue';
import OrgSignUpForm from '@/components/OrgSignUpForm.vue';
import OutlineLaptop from '@/components/OutlineLaptop.vue';
import OutlinePhone from '@/components/OutlinePhone.vue';
import RecipientPreviewEmail from '@/components/RecipientPreviewEmail.vue';
import RecipientPreviewText from '@/components/RecipientPreviewText.vue';
import SelectThemeRibbon from '@/components/SelectThemeRibbon.vue';
import SideScroller from '@/components/SideScroller.vue';
import SignInDialog from '@/components/SignInDialog.vue';
import DialogScrollLockLoader from '@/components/DialogScrollLockLoader.vue';
import VVirtualScrollGrid from '@/components/VVirtualScrollGrid.vue';
import { getBrightness, getColorDistance, getContrastRatio } from '@/utils/group-video-template.js';
import { processImageFile, scale as scaleImg } from '@/utils/image.js';
import { DEFAULT, EFFECTS } from '@/utils/skin.js';
import { colors as COLORS, fonts as FONTS } from '@/card-customization-options.js';
import iconPathEnvBackground from 'raw-loader!@/assets/icon-path-env-background.txt';
import iconPathEnvColor from 'raw-loader!@/assets/icon-path-env-color.txt';
import iconPathEnvFont from 'raw-loader!@/assets/icon-path-env-font.txt';
import iconPathEnvStamp from 'raw-loader!@/assets/icon-path-env-stamp.txt';
import iconPathEnvToFrom from 'raw-loader!@/assets/icon-path-env-to-from.txt';
import { mdiClose, mdiPlus, mdiEmail, mdiFormatFont, mdiFormatColorText, mdiCellphoneLink, mdiGrain, mdiImage, mdiMagnify, mdiMusic, mdiMusicOff, mdiPalette, mdiPencil, mdiSwapHorizontalVariant, mdiTag, mdiUpload, mdiLaptop, mdiCellphone, mdiTextBox, mdiArrowCollapseDown, mdiArrowCollapseUp, mdiArrowCollapseRight, mdiArrowCollapseLeft, mdiMenuDown, mdiMenuUp, mdiDelete, mdiChevronRight, mdiGift, mdiFlower, mdiCat, mdiApps, mdiDotsCircle, mdiImageFilterHdr, mdiMessage, mdiHeart, mdiShuffleVariant, mdiAlert } from '@mdi/js';


// When you add a new background, you need to upload the original file (with the matching name) to /page_packgrounds/
// in the ellacard-public S3 bucket. You also need to upload a version that matches the name but with the string
// ".160x90" injected before the extension (for efficient previews). You can generate the preview using the following
// ffmpeg command:
// ffmpeg -i resource.mp4 -vf scale=160:90 resource.160x90.mp4
//
// For videos, you also need to make sure you upload a poster. You can automatically extract the first frame using the
// following:
// ffmpeg -i resource.mp4 -vframes 1 resource.poster.jpg
//
// Use this command on videos to make sure they're 1920x1080 and fully optimized for web:
// ffmpeg -i in.mp4 -vf scale=1920:1080 -movflags +faststart out.mp4
//
// To prepare all the videos in a directoy, including generating the JSON (after you've renamed them):
// mkdir output
// for i in *; do ffmpeg -i "$i" -vf scale=1920:1080 -movflags +faststart "output/${i%.*}.mp4"; done
// # Manually filter out the ones that are too big / didn't compress well
// cd output
// for i in *; do ffmpeg -i "$i" -vframes 1 "${i%.*}.poster.jpg"; ffmpeg -i "$i" -vf scale=160x90 -movflags +faststart "${i%.*}.160x90.mp4"; done
// # NOTE: be sure to replace TAG with whatever tag you want these resources to have by default
// for i in *.160x90.*; do BG=$(ffmpeg -loglevel error -i "$i" -vf palettegen=max_colors=4 -f image2 - | magick -[1x1+1+1] txt: | grep -oE '#[0-9A-F]{6}'); echo "  [ \"${i%.160x90*}.${i##*.}\", \"$BG\", [ \"TAG\" ] ],"; done | pbcopy
// # Then upload the contents of the 'output' directory to ellacard-public:page_backgrounds/ in S3 (and make sure you
// # set "Grant public-read access")
//
// You may find the following command useful for automatically cropping / scaling images using ImageMagick:
// magick convert in.jpg -gravity center -crop 16:9 -resize 1920x1080 -quality 75 out.jpg
//
// To generate thumbnails for all the files in the current directory (images and videos):
// for i in *; do ffmpeg -i "$i" -vf scale=160:90 "${i%.*}.160x90.${i##*.}"; done
//
// To generate the JSON array-tuples to add to the 'media-library-background.json' list and copy it to your clipboard:
// for i in *.160x90.*; do BG=$(ffmpeg -loglevel error -i "$i" -vf palettegen=max_colors=4 -f image2 - | magick -[1x1+1+1] txt: | grep -oE '#[0-9A-F]{6}'); echo "  [ \"${i%.160x90*}.${i##*.}\", \"$BG\" ],"; done | pbcopy
//
// Then you have to pair it with an appropriate hex color (shows during loading and in email). You mostly did this by
// sampling a color from the actual resource using the "Digital Color Meter" and you may find the following function
// useful for quickly converting those tab-delimited RGB strings into hex codes:
// s => '#' + s.split(/\s+/).slice(0, 3).map(x => parseInt(x).toString(16).padStart(2, '0')).join('')


// When you add new stamps you need to rely on the custom-stamp-promp to process them - uncomment the 'processStamps'
// button at the top of this file and the corresponding 'processStamps' method to add the stamp border to a bunch of
// resources at once.


// There is a bug with the v-color-picker where if it gets a value like '#xxx' it automatically expands it to a
// representation like '#xxxxxxFF', which results in the color picker showing an alpha channel -->
function vColorPickerValue(v) {
  return v ? (/^#...$/.test(v) ? '#' + v[1] + v[1] + v[2] + v[2] + v[3] + v[3] : v).toUpperCase() : '';
}


// All of our media should be prefixed by this URL
const MEDIA_PREFIX = 'https://cdn.ellacard.com/';
const MEDIA_PREFIX_LEGACY = 'https://ellacard-public.s3.amazonaws.com/';

function getMediaName(url) {
  url = url.replace(MEDIA_PREFIX_LEGACY, MEDIA_PREFIX);
  if (!url.startsWith(MEDIA_PREFIX))
    return (url.length >= 40) ? (url.slice(0, 40) + '…') : url;
  const name = url.slice(MEDIA_PREFIX.length).split('/').last;
  return name.includes('.') ? name.split('.')[0] : atob(name).split('.')[0];
}


const STAMP_FILTERS = [
  [ 'All' ],
  [ 'Flowers', mdiFlower ],
  [ 'Animals', mdiCat ],
  [ 'Patterns', mdiApps ],
  [ 'Romantic', mdiHeart ],
  [ 'Holiday', mdiGift ]
];


const BG_FILTERS = [
  [ 'All' ],
  [ 'Animated', mdiDotsCircle ],
  [ 'Nature', mdiImageFilterHdr ],
  [ 'Patterns', mdiApps ],
  [ 'Romantic', mdiHeart ],
  [ 'Holiday', mdiGift ]
];


export default {
  name: 'order-stepper-theme',

  components: {
    AudioPreviewBtn,
    CardBackground,
    ColorPrompt,
    Confetti,
    CustomStampPrompt,
    Envelope,
    FontPickerBtn,
    MusicLibraryDialog,
    OrgSignUpForm,
    OutlineLaptop,
    OutlinePhone,
    SelectThemeRibbon,
    SideScroller,
    SignInDialog,
    DialogScrollLockLoader,
    VVirtualScrollGrid
  },

  props: [
    'order',
    'orgSkin',
    'noFooter', // Hides the presets and view modes
    'noCustomMedia', // Prevents users from uploading custom media (they'd have to sign in otherwise)
    'hideBackgroundConfetti', // Prevents the background and the confetti from showing
    'disableTo',
    'disableFrom',
    'disableMultipleRecipients',
    'disableLogo',
    'deliveries'
  ],

  data() {
    return {
      showSignIn: false,
      showMoreColors: false,
      showEditBlob: null,
      blobFrom: null,
      blobTo: null,
      useSameLogo: true,
      promptColor: null,
      promptStamp: null,
      bgFilter: 0,
      stampFilter: 0,
      logoKey: 'header_logo',
      selected: null,
      filter: null,
      tooltips: false,
      confetti: {
        show: false,
        opts: _.cloneDeep(EFFECTS.confetti)
      },

      media: {
        background: [],
        stamp: []
      },
      loadMediaStatus: this.$track('loadMedia'),

      customMedia: {
        background: [],
        stamp: [],
        logo: []
      },
      loadCustomMediaStatus: this.$track('loadCustomMedia'),
      addMediaFromDataURLStatus: this.$track('addMediaFromDataURL'),

      deletingMedia: null,
      deleteMediaStatus: this.$track('deleteMedia')
    };
  },

  computed: {
    envFrom() {
      return this.order.info.from || '';
    },

    envTo() {
      return this.order.multiple_recipients ? '[&nbsp;recipient&nbsp;name&nbsp;]' : (this.order.info.to || '');
    },

    musicName() {
      const url = this.order.skin.music;
      return url ? getMediaName(url) : '';
    },

    isCancelled() {
      return this.order.state == 'CANCELLED';
    },

    selectedEffectPreset() {
      const e = this.order.skin.open_effect;
      if (!e)
        return 'None';
      const selected = EFFECTS[e] || JSON.parse(e);
      for (const [ name, cfg ] of Object.entries(EFFECTS))
        if (_.isEqual(selected, cfg))
          return name;
      return null;
    },

    orgHasLogo() {
      return [ 'header_logo', 'email_footer_logo', 'email_footer_url' ].every(this.orgHas);
    },

    envLowContrast() {
      const s = this.order.skin;
      if (getContrastRatio(s.env_background_color, s.env_font_color) < 3.5)
        return true;
      if (getColorDistance(s.env_background_color, s.env_font_color) < 100)
        return true;
      return false;
    }
  },

  created() {
    Object.assign(this, { RecipientPreviewEmail, RecipientPreviewText, COLORS, DEFAULT, EFFECTS, EFFECT_NAMES: Object.keys(EFFECTS), FONTS, BG_FILTERS, STAMP_FILTERS, getBrightness, iconPathEnvBackground, iconPathEnvColor, iconPathEnvFont, iconPathEnvStamp, iconPathEnvToFrom, mdiClose, mdiEmail, mdiFormatFont, mdiFormatColorText, mdiCellphoneLink, mdiGrain, mdiImage, mdiMagnify, mdiMusic, mdiMusicOff, mdiPalette, mdiPencil, mdiPlus, mdiSwapHorizontalVariant, mdiTag, mdiUpload, mdiLaptop, mdiCellphone, mdiTextBox, mdiArrowCollapseDown, mdiArrowCollapseUp, mdiArrowCollapseRight, mdiArrowCollapseLeft, vColorPickerValue, mdiMenuDown, mdiMenuUp, mdiDelete, mdiChevronRight, mdiMessage, mdiShuffleVariant, mdiAlert });
  },

  methods: {
    validate() {
      [ 'from', 'to' ].map(x => this.$refs.env.dirtyBlobIds.add(x));
      return this.$refs.env.blobs.every(x => !x.last);
    },

    // processStamps() {
    //   const el = document.createElement('input');
    //   el.type = 'file';
    //   el.setAttribute('multiple', '');
    //   document.body.appendChild(el);
    //   el.onchange = async e => {
    //     for (const file of e.target.files)
    //       await new Promise(async r => {
    //         const opts = await processImageFile(file);
    //         this.promptStamp = { ...opts, file, scale: true, border: true };
    //         await new Promise(q => setTimeout(q, 500));
    //         const link = document.createElement('a');
    //         link.href = this.$refs.stamp.$refs.canvas.toDataURL('image/png');
    //         link.download = file.name;
    //         document.body.appendChild(link);
    //         link.click();
    //         link.remove();
    //         this.promptStamp = null;
    //         await this.$nextTick();
    //         r();
    //       });
    //   };
    //   el.click();
    // },

    isActive(type) {
      const s = this.order.skin;
      switch (type) {
        case 'BG':
          return !!s.page_background_img || s.page_background_color != DEFAULT.page_background_color;
        case 'CF':
          return !!s.open_effect;
        case 'MUS':
          return !!s.music;
        case 'STMP':
          return s.env_stamp != DEFAULT.env_stamp;
        case 'ENV':
          for (const key of [ 'env_background_color', 'env_font_color' ])
            if (vColorPickerValue(s[key]) != vColorPickerValue(DEFAULT[key]))
              return true;
          return false;
        case 'FONT':
          return s.env_font != DEFAULT.env_font;
        case 'LOGO':
          for (const key of [ 'header_logo', 'email_footer_logo', 'email_footer_url' ])
            if (s[key] != DEFAULT[key])
              return true;
          return false;
      }
    },

    orgHas(k) {
      return !!this.orgSkin?.hasOwnProperty(k);
    },

    async promptImage(callback) {
      if (!this.isSignedIn) {
        await this.$refs['sign-in'].prompt();
        if (!this.isSignedIn)
          return;
      }

      const el = this.$refs.upload;
      el.onchange = e => {
        const file = e.target.files[0];
        if (file)
          processImageFile(file).then(opts => callback({ ...opts, file }));
        e.target.onchange = null;
        e.target.value = null;
      };
      el.click();
    },

    getRecipientTarget(r) {
      if (r.email)
        return r.email;
      if (r.phone)
        return this.$options.filters.phone(r.phone);
      let address = r.address.line_1 + '\n';
      if (r.address.line_2)
        address += r.address.line_2 + '\n';
      return address + r.address.city + ', ' + r.address.state + ' ' + r.address.code;
    },

    set(props) {
      Object.assign(this.order.skin, props);
    },

    setConfetti() {
      this.set({ open_effect: this.confetti.show ? JSON.stringify(this.confetti.opts) : null });
    },

    selectEffectPreset(v) {
      const effect = EFFECTS[v];
      this.confetti.show = !!effect;
      if (effect)
        this.$set(this.confetti, 'opts', _.cloneDeep(effect));
      this.setConfetti();
    },

    getBackgroundPreview(item) {
      if (!item.default)
        return item.url;
      const parts = item.url.split('.');
      parts.splice(parts.length - 1, 0, '160x90');
      return parts.join('.');
    },

    alertOrgProp(prop) {
      // This should be defunct now that I explicitly hide the inputs if the org skin does not allow them to be set
      this.$confirm({
        title: 'Organization override',
        msg: `Your organization does not allow you to customize the ${prop}`,
        buttons: [ 'Ok' ],
        primary: 'Ok'
      });
    },

    click(type) {
      if (this.isCancelled)
        return;
      switch (type) {
        case 'logo':
          if (!this.orgHasLogo) {
            this.logoKey = 'header_logo';
            this.selected = 'LOGO';
          }
          break;
        case 'stamp':
          if (!this.orgHas('env_stamp'))
            this.selected = 'STMP';
          break;
      }
    },

    async addMediaFromDataURL(desiredName, type, dataURL) {
      const base = desiredName.replace(/\.[^/.]+$/, '');
      let name = base;
      for (let i = 2; this.customMedia[type].find(x => x.name == name); i++)
        name = base + ' ' + i;
      const blob = await fetch(dataURL).then(r => r.blob());
      const file = new File([ blob ], name, { type: blob.type });
      const url = await this.$call('upload-to-user-media-library', { name, type, file });
      this.customMedia[type].push({ name, url });
      this.customMedia[type].sort();
      return url;
    },

    async loadMedia(type, loadDefault) {
      if (this.media[type]._isLoaded)
        return;
      this.media[type] = await loadDefault();
      this.media[type]._isLoaded = true;
    },

    async loadCustomMedia(type) {
      if (!this.isSignedIn)
        return;
      if (this.customMedia[type]._forUserId == this.global.profile.user_id)
        return;
      this.customMedia[type] = await this.$call('list-user-media-library', { type });
      this.customMedia[type]._forUserId = this.global.profile.user_id
    },

    async onUploadBackground(opts) {
      const url = await this.addMediaFromDataURL(opts.file.name, 'background', opts.url);
      if (!this.orgHas('page_background_img'))
        this.set({ page_background_img: url });
    },

    async onUploadLogo(opts) {
      const url = await this.addMediaFromDataURL(opts.file.name, 'logo', opts.url);
      this.setLogo(url);
    },

    setLogo(url) {
      const toSet = {};
      for (const key of (this.useSameLogo ? [ 'header_logo', 'email_footer_logo' ] : [ this.logoKey ]))
        if (!this.orgHas(key))
          toSet[key] = url;
      this.set(toSet);
    },

    async deleteMedia(type, name) {
      this.deletingMedia = name;
      try {
        const r = await this.$confirm({ title: 'Are you sure?', msg: 'Are you sure you want to delete this item?' });
        if (r == 'No')
          return false;
        await this.$call('delete-from-user-media-library', { type, name });
        this.customMedia[type].splice(this.customMedia[type].findIndex(x => x.name == name), 1);
        return true;
      } finally {
        this.deletingMedia = null;
      }
    },

    async setMultipleRecipients(v) {
      const o = this.order;
      const n = o.info.recipient_groups.length - 1;
      if (!v && (n > 0)) {
        const r = await this.$confirm({
          title: `Remove ${n} extra recipient${(n == 1) ? '' : 's'}?`,
          msg: 'Are you sure you want to send this card to <i>one person only</i>? The extra recipients you already added will be removed.'
        });
        if (r == 'No')
          return;
        o.info.recipient_groups = o.info.recipient_groups.slice(0, 1);
      }
      o.multiple_recipients = v;
    }
  },

  watch: {
    'order.info.to'(v) {
      if (!v && !this.order.multiple_recipients && this.order.info.recipient_groups[0]) {
        const { targets } = this.order.info.recipient_groups[0];
        if (!targets.some(x => x))
          this.order.info.recipient_groups = [];
      }
    },

    'order.skin': {
      immediate: true,
      handler(v) {
        if (v.email_footer_logo != v.header_logo)
          this.useSameLogo = false;
      }
    },

    'order.skin.open_effect': {
      immediate: true,
      deep: true,
      handler(v) {
        this.confetti = { show: !!v, opts: v ? _.cloneDeep(EFFECTS[v]) || JSON.parse(v) : this.confetti.opts };
      }
    },

    selected(v) {
      if (v == 'CF') {
        this.confetti.show = true;
        this.setConfetti();
      }

      const loadMediaHint = {
        BG: {
          type: 'background',
          loadDefault: async () => (await import('@/utils/media-library-background.json')).default.map(([ name, color, tags ]) => ({ name, url: `https://cdn.ellacard.com/page_backgrounds/${name}`, color, tags: tags || [], default: true }))
        },
        STMP: {
          type: 'stamp',
          loadDefault: async () => (await import('@/utils/media-library-stamp.json')).default.map(([ name, tags ]) => ({ name, url: `https://cdn.ellacard.com/stamps/${name}`, tags, default: true }))
        },
        LOGO: {
          type: 'logo'
        }
      }[v];
      if (loadMediaHint) {
        this.loadCustomMedia(loadMediaHint.type);
        if (loadMediaHint.loadDefault)
          this.loadMedia(loadMediaHint.type, loadMediaHint.loadDefault);
      }
    }
  }
}
</script>
